﻿import React, { useContext, useState, useEffect, useRef, useLayoutEffect } from 'react';
import ReactDOM from 'react-dom';

import { GoogleMap } from '@react-google-maps/api';
import { FilterFacilities } from './Filters'
import { search } from '../Endpoints'
import { calculateDistance, removeDuplicatedFacilities } from '../Helpers'
import { addMarkersAndClusterer } from '../../../_Shared/Scripts/MapsMarkersAndClusterer';

import MapControlsWidget from './MapControls'
import MapHeaderWidget from './MapHeader'
import LoadingScreenWidget from './LoadingScreen'
import InfoWindowWidget from './InfoWindow'

import MapMarkerIcon from '../../../../../publicResources/icons/map-marker-unselected.svg'
import MapMarkerIconSelected from '../../../../../publicResources/icons/map-marker-selected.svg'
import MapMarkerRoofIcon from '../../../../../publicResources/icons/map-marker-roof-unselected.svg'
import MapMarkerRoofIconSelected from '../../../../../publicResources/icons/map-marker-roof-selected.svg'
import MapMarkerIconCenter from '../../../../../publicResources/icons/map-marker-red.svg'
import { MarkerClusterer } from '@googlemaps/markerclusterer';

let mapOptions = {
  mapId: 'c450492765080973',
  minZoom: 8,
  maxZoom: 18,
  disableDefaultUI: true,
  clickableIcons: false,
  gestureHandling: 'greedy',
};

const MapWidget = ({ isLoaded, facilitySearchContext, setFacilitySearchContext, viewToggle }) => {
  //React maps: https://www.npmjs.com/package/@react-google-maps/api 

  //Temporary disable map on GoLive
  if (facilitySearchContext.localization.messages.goLiveMessage) mapOptions = { ...mapOptions, zoomControl: false, gestureHandling: 'none', keyboardShortcuts: false };

  const lang = facilitySearchContext.localization.inputs;

  const refContainer = useRef(null);

  const [requestAnimation, setRequestAnimation] = useState(false);
  const [maxHeight, setMaxHeight] = useState(null);
  const [mapRef, setMapRef] = useState(null);
  const [zoom, setZoom] = useState(facilitySearchContext.selectedLocation?.address ? 15 : facilitySearchContext.selectedLocation?.name === 'georesult' ? 12 : 10);
  const [lastKnownZoom, setLastKnownZoom] = useState(facilitySearchContext.selectedLocation?.address ? 15 : facilitySearchContext.selectedLocation?.name === 'georesult' ? 12 : 10);
  const [center, setCenter] = useState(facilitySearchContext.selectedLocation.position ?? { lat: 59.329, lng: 18.068 });
  const [centerMarker, setCenterMarker] = useState(null);
  const [lastKnownLocation, setLastKnownLocation] = useState(facilitySearchContext.selectedLocation.position ?? { lat: 59.329, lng: 18.068 });
  const [markerClusterer, setMarkerClusterer] = useState(null);

  const [reloadButtonVisible, setReloadButtonVisible] = useState(false);
  const [reloadButtonPending, setReloadButtonPending] = useState(false);

  const [facilities, setFacilities] = useState([]);
  const [markerMap, setMarkerMap] = useState({});

  const [infoOpen, setInfoOpen] = useState(false);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [activeMarker, setActiveMarker] = useState(null);
  const [keyboardEvent, setKeyboardEvent] = useState(false);
  const [disableReload, setDisableReload] = useState(false);

  const contextMapPosition = facilitySearchContext.selectedLocation?.position ?? { lat: 59.329, lng: 18.068 };
  const returnToOriginalPosition = facilitySearchContext.returnToOriginalPosition;
  const contextMapZoom = facilitySearchContext.mapZoom;
  const contextLastKnownLocation = facilitySearchContext.lastKnownLocation;
  const contextFilteredFacilities = facilitySearchContext.filteredFacilities;

  const getZIndex = (lat) => Math.round((lat - Math.floor(lat)) * -10000);

  const removeEmptyInfoWindows = () => {
    //Check if empty infowindows are open and close them
    const infoWindows = document.querySelectorAll('div.gm-style-iw-a');
    if (!infoWindows.length) return;
    infoWindows.forEach(infoWindow => !infoWindow.querySelector('.facilitysearchpage__map__info') && infoWindow.querySelector('button[title="Close"]').click());
  }

  useEffect(() => {
    if (!mapRef || !facilities) return;
    const clusterer = addMarkersAndClusterer(mapRef, facilities, markerClickHandler, markerLoadHandler, markerMap, markerClusterer, facilitySearchContext.updateMarkers);
    if (clusterer) setMarkerClusterer(clusterer);
  }, [mapRef, facilities])

  useEffect(() => {
    if (!mapRef || !facilitySearchContext.selectedLocation?.address) return;
    if (centerMarker) centerMarker.map = null;
    const icon = { url: MapMarkerIconCenter, scaledSize: new google.maps.Size(26, 26), anchor: new google.maps.Point(13, 26) };

    const marker = new google.maps.marker.AdvancedMarkerElement({
      map: mapRef,
      position: center,
      content: setImage(icon),
      zIndex: getZIndex(center.lat) - 50
    });
    setCenterMarker(marker);
  }, [mapRef, center])

  useEffect(() => {
    if (facilitySearchContext.selectedLocation?.position)
      setCenter(facilitySearchContext.selectedLocation.position);
    if (zoom && mapRef) mapRef.setZoom(zoom);
  }, [contextMapPosition])

  useEffect(() => {
    if (facilitySearchContext.mapZoom !== zoom) {
      setZoom(facilitySearchContext.selectedLocation?.address ? facilitySearchContext.mapZoom : facilitySearchContext.selectedLocation?.name === 'georesult' ? 12 : 10);
    }
  }, [contextMapZoom])

  useEffect(() => {
    if (!returnToOriginalPosition || !mapRef) return;
    setFacilitySearchContext({ ...facilitySearchContext, returnToOriginalPosition: false });
    mapRef.panTo(center);
    if (mapRef.getZoom() !== zoom) mapRef.setZoom(zoom);
  }, [returnToOriginalPosition])

  useEffect(() => {
    if (facilitySearchContext.mapZoom !== zoom) {
      setZoom(facilitySearchContext.selectedLocation?.address ? facilitySearchContext.mapZoom : facilitySearchContext.selectedLocation?.name === 'georesult' ? 12 : 10);
    }
  }, [facilitySearchContext.selectedLocation])

  useEffect(() => {
    if (contextLastKnownLocation !== lastKnownLocation && contextLastKnownLocation)
      setLastKnownLocation(contextLastKnownLocation);
  }, [contextLastKnownLocation])

  useEffect(() => {
    if (!isLoaded || !contextFilteredFacilities)
      return;
    setFacilities(contextFilteredFacilities);
  }, [isLoaded, contextFilteredFacilities])

  useEffect(() => {
    if (facilitySearchContext.panOnLoad && facilitySearchContext.filteredFacilities?.length > 0 && mapRef?.getBounds() && facilitySearchContext.visibleFacilities?.length === 0) {
      const facilitiesInBounds = checkFacilitiesInBounds(contextFilteredFacilities);
      if ((viewToggle !== 'list' || window.innerWidth > 988) && facilitiesInBounds?.markersInBounds.length === 0) panMap();
    }
  }, [facilitySearchContext.filteredFacilities, mapRef?.getBounds(), facilitySearchContext.visibleFacilities, viewToggle])

  useEffect(() => {
    if (!facilitySearchContext.updateMapBounds || !mapRef || (viewToggle === 'list' && window.innerWidth < 989))
      return;

    setDisableReload(true);
    fitBounds();
    setFacilitySearchContext({ ...facilitySearchContext, updateMapBounds: false });
  }, [facilitySearchContext.updateMapBounds, viewToggle]);

  const panMap = () => {
    const closestFacility = { lat: contextFilteredFacilities[0].Address.Marker.Coordinates[1], lng: contextFilteredFacilities[0].Address.Marker.Coordinates[0] };
    if (window.innerWidth < 769) mapRef.setZoom(zoom - 1);
    //else mapRef.setZoom(zoom - 1);
    mapRef.panTo(closestFacility);
    setTimeout(() => {
      mapRef.setZoom(zoom);
    }, 500);
  }

  const setImage = (icon, img) => {
    const image = img ?? document.createElement('img');
    image.setAttribute('src', icon.url);
    image.setAttribute('height', icon.scaledSize.height);
    image.setAttribute('width', icon.scaledSize.width);
    return image;
  }

  const handleClose = () => {
    setInfoOpen(false);
    setActiveMarker(null);
    if (markerMap && Object.keys(markerMap).length > 0) {
      for (const [key, value] of Object.entries(markerMap)) {
        const facility = facilities.find(facility => facility.Uid === key);
        const icon = facility?.Indoor ? { url: MapMarkerRoofIcon, scaledSize: new google.maps.Size(30, 33), anchor: new google.maps.Point(15, 33) } :
          { url: MapMarkerIcon, scaledSize: new google.maps.Size(30, 33), anchor: new google.maps.Point(15, 33) };
        value.content = setImage(icon);
      }
    }
    if (facilitySearchContext.showOnMap) setFacilitySearchContext({ ...facilitySearchContext, showOnMap: null });
  }

  const checkFacilitiesInBounds = (facilities) => {
    if (!facilities) return
    const mapBounds = mapRef.getBounds();
    const markersInBounds = [];

    if (mapBounds) {
      for (var i = 0; i < facilities.length; i++) {
        const coordinates = {
          lng: facilities[i].Address.Marker.Coordinates[0],
          lat: facilities[i].Address.Marker.Coordinates[1],
        };

        if (mapBounds.contains(coordinates)) {
          markersInBounds.push(facilities[i]);
        }
      }
    }
    return { markersInBounds, mapBounds }
  }

  const fitBounds = () => {
    if (!mapRef || !facilities)
      return;

    const bounds = new window.google.maps.LatLngBounds();
    const filteredFacilities = contextFilteredFacilities ?? facilities;

    for (var i = 0; i < filteredFacilities.length; i++) {
      const coordinates = {
        lng: filteredFacilities[i].Address.Marker.Coordinates[0],
        lat: filteredFacilities[i].Address.Marker.Coordinates[1],
      };
      bounds.extend(coordinates);
    }

    mapRef.fitBounds(bounds);
  };

  const loadHandler = map => {
    setMapRef(map);

    if (refContainer) {
      setFacilitySearchContext({ ...facilitySearchContext, mapElementInDom: refContainer.current });
    }
  };

  const onIdleHandler = () => {
    if (!mapRef || !facilities)
      return;

    removeEmptyInfoWindows();
    const mapBounds = mapRef.getBounds();
    let markersInBounds = [];

    if (mapBounds) {
      ({ markersInBounds } = checkFacilitiesInBounds(facilities))
      const currentCenterLocation = mapRef.center.toJSON();
      const idleLastKnownLocation = contextLastKnownLocation ?? lastKnownLocation;

      if (idleLastKnownLocation) {
        const distanceSinceLastLocation = calculateDistance(
          { latitude: currentCenterLocation.lat, longitude: currentCenterLocation.lng },
          { latitude: idleLastKnownLocation.lat, longitude: idleLastKnownLocation.lng }
        )

        const zoomOut = mapRef.getZoom() < 14 && mapRef?.getZoom() < lastKnownZoom;
        setLastKnownZoom(mapRef?.getZoom());

        if (!disableReload && !facilitySearchContext.panOnLoad && !reloadButtonVisible && (distanceSinceLastLocation && (distanceSinceLastLocation > 1000 || markersInBounds.length === 0) || zoomOut)) {
        //if (!reloadButtonVisible && (distanceSinceLastLocation && (distanceSinceLastLocation > 1000 || markersInBounds.length === 0))) {

          reloadMapClickHandler()
          //setReloadButtonVisible(true);
          //} else if(reloadButtonVisible) {
          //  setReloadButtonVisible(false)
        }
      }
    }
    setFacilitySearchContext({ ...facilitySearchContext, visibleFacilities: markersInBounds, mapBounds, updateMarkers: false });
  };

  const markerLoadHandler = (marker, place, index) => {
    let prevMarkerMap = markerMap;
    prevMarkerMap[place.Uid] = marker;
    setMarkerMap(prevMarkerMap);

    if (facilitySearchContext.showLoadingScreen && facilitySearchContext.filteredFacilities && index === facilitySearchContext.filteredFacilities.length - 1) {
      const facilitiesInBounds = checkFacilitiesInBounds(facilitySearchContext.filteredFacilities);
      setFacilitySearchContext({ ...facilitySearchContext, visibleFacilities: facilitiesInBounds?.markersInBounds, showLoadingScreen: false });
    }
  };

  useEffect(() => {
    if (!facilitySearchContext.showOnMap) return;
    if (mapRef.getZoom() < 15) mapRef.setZoom(15);
    markerClickHandler(null, facilitySearchContext.showOnMap);
  }, [facilitySearchContext.showOnMap])

  const markerClickHandler = async (event, place) => {
    handleClose();
    setKeyboardEvent(false);
    if (event && event.domEvent instanceof KeyboardEvent) {
      setKeyboardEvent(true);
    }
    const icon = place.Indoor ? { url: MapMarkerRoofIconSelected, scaledSize: new google.maps.Size(40, 49), anchor: new google.maps.Point(20, 49) } :
      { url: MapMarkerIconSelected, scaledSize: new google.maps.Size(40, 49), anchor: new google.maps.Point(20, 49) }
    markerMap[place.Uid].content = setImage(icon);
    setSelectedPlace(place);
    setActiveMarker({ pos: [place.Address.Marker.Coordinates.Lng ?? place.Address.Marker.Coordinates[0], place.Address.Marker.Coordinates.Lat ?? place.Address.Marker.Coordinates[1]] });
    if (!event) setTimeout(() => setInfoOpen(true), 200);
    else setInfoOpen(true);
  };

  const reloadMapClickHandler = async () => {
    if (!mapRef)
      return;

    setReloadButtonPending(true);

    //if (facilitySearchContext.selectedLocation?.name) {
    //  mapRef.setOptions({ minZoom: 8 });
    //setZoom(15);
    //}

    const currentCenterLocation = mapRef.center.toJSON();
    const mapBounds = mapRef.getBounds();
    const markersInBounds = [];
    const newRadius = calculateDistance(
      { latitude: currentCenterLocation.lat, longitude: currentCenterLocation.lng },
      { latitude: currentCenterLocation.lat, longitude: mapBounds.toJSON().east },
      { unit: 'km' }
    );
    setReloadButtonVisible(false);
    setFacilitySearchContext({ ...facilitySearchContext, showLoadingScreen: true });
    const newFacilities = await search.getNearbyFacilities({ lang: facilitySearchContext.episerver.selectedlanguage, location: center, reloadLocation: currentCenterLocation, radius: Math.ceil(newRadius) }, false);

    if (newFacilities.status !== 200)
      return;

    const allFacilities = removeDuplicatedFacilities([...newFacilities.body, ...facilitySearchContext.allFacilities]);
    const filteredFacilities = FilterFacilities(allFacilities, facilitySearchContext.filterSelection);

    if (mapBounds) {
      for (var i = 0; i < filteredFacilities.length; i++) {
        const coordinates = {
          lng: filteredFacilities[i].Address.Marker.Coordinates[0],
          lat: filteredFacilities[i].Address.Marker.Coordinates[1],
        };

        if (mapBounds.contains(coordinates)) {
          markersInBounds.push(filteredFacilities[i]);
        }
      }
    }

    setReloadButtonPending(false);
    //setReloadButtonVisible(false);
    setLastKnownLocation(currentCenterLocation);

    setFacilitySearchContext({ ...facilitySearchContext, allFacilities, filteredFacilities, visibleFacilities: markersInBounds, mapBounds, lastKnownLocation: currentCenterLocation });
  };

  const initMaxHeight = (e) => {
    if (!requestAnimation) {
      window.requestAnimationFrame(() => {
        if (facilitySearchContext.mapViewOpen)
          setMaxHeight({ maxHeight: `${window.innerHeight}px` });
        else
          setMaxHeight(null);

        setRequestAnimation(false);
      });
    }

    setRequestAnimation(false);
  };

  useEffect(() => {
    if (facilitySearchContext.mapViewOpen)
      window.addEventListener('resize', initMaxHeight);
    else
      window.removeEventListener('resize', initMaxHeight);

    () => window.removeEventListener('resize', initMaxHeight);
  }, [facilitySearchContext.mapViewOpen])

  const renderMap = (lang) => {
    const markerIconCenter = { url: MapMarkerIconCenter, scaledSize: new google.maps.Size(26, 26), anchor: new google.maps.Point(13, 26) }

    return (
      <div data-view-toggled={viewToggle === 'map' ? true : false} className="facilitysearchpage__wrapper__map">
        <div className={['facilitysearchpage__map', facilitySearchContext.mapViewOpen ? 'facilitysearchpage__map--fullscreen' : null].join(' ')} ref={refContainer} style={maxHeight}>
          {reloadButtonVisible && <button className="facilitysearchpage__map__reload" disabled={reloadButtonPending} onClick={reloadMapClickHandler}>{lang.reloadmap}</button>}
          {facilitySearchContext.mapViewOpen && <MapHeaderWidget facilitySearchContext={facilitySearchContext} setFacilitySearchContext={setFacilitySearchContext} mapContainerRef={refContainer} />}
          {facilitySearchContext.showLoadingScreen && <LoadingScreenWidget />}
          <GoogleMap
            onLoad={loadHandler}
            onIdle={onIdleHandler}
            onClick={handleClose}
            onDrag={() => setDisableReload(false)}
            center={center}
            zoom={zoom}
            options={mapOptions}
            mapContainerStyle={{ height: "100%", width: "100%" }}>
            {infoOpen && selectedPlace && <InfoWindowWidget infoOpen={infoOpen} keyboardEvent={keyboardEvent} facilitySearchContext={facilitySearchContext} anchor={markerMap[selectedPlace.Uid]} selectedPlace={selectedPlace} onClose={handleClose} />}
          </GoogleMap>
          {mapRef && <MapControlsWidget map={mapRef} />}
        </div>
      </div>
    );
  };

  return isLoaded ? renderMap(lang) : <div className="facilitysearchpage__map"><div className="facilitysearchpage__map__overlay"><div className="aimo-spinner"><span></span><span></span><span></span><span></span></div></div></div>;
}

export default MapWidget;

